import CloseIcon from "@mui/icons-material/Close";
import { Box, Button, IconButton, useMediaQuery, useTheme } from "@mui/material";
import {
  GridColumnVisibilityModel,
  GridPinnedColumns,
  GridRowModel,
  GridSortModel,
} from "@mui/x-data-grid-pro";
import { GridApiPro } from "@mui/x-data-grid-pro/models/gridApiPro";
import { closeSnackbar, enqueueSnackbar, SnackbarKey } from "notistack";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CachePolicies } from "use-http";

import { JANZU_URL } from "config/config";
import { columnUIToInternal } from "config/LabelMapping";
import { QueryDetailsModal } from "features/queryDetailsModal/QueryDetailsModal";
import { useAppDispatch, useAppSelector } from "hooks/store";
import { useFetchCustom } from "hooks/useFetchCustom";
import { StyledDataGrid } from "new-components/dataGrid/StyledDataGrid";
import { ConfirmDeleteModal } from "new-components/modals/ConfirmDeleteModal";
import { getImageQueries } from "store/services/ImageQueries/imageQueriesThunks";
import { setPage, setPageSize } from "store/slices/ImageQueries/imageQueriesSlice";
import { ListResponse } from "types/api";
import { Detector } from "types/detectors";
import { ImageQuery } from "types/imageQueries";
import { extractPaginateFrom } from "utils/utilityFunctions";

import { CursorPagination } from "../../DataPage";

import { enhanceColumnWithDeleteModal, getColumns } from "./columnsComponents/columnsComponents";
import { NoRowsOverlay } from "./noRowsOverlay/NoRowsOverlay";

// Constants
const DEFAULT_MAX_TO_REFRESH = 20;

// Props interface
interface DataGridProps {
  apiRef: React.MutableRefObject<GridApiPro>;
  detector?: Detector;
  isDetectorLoading: boolean;
  imageQueryUrl: string;
  columnVisibilityModel: GridColumnVisibilityModel;
  onChangeColumnVisibilityModel: (newModel: GridColumnVisibilityModel) => void;
  filterState: Record<string, any>;
  onFilterStateChange: (objToUpdate: Record<string, string>) => void;
  onChangeCursorPagination: (cursorPagination: CursorPagination) => void;
  filterableColumns: string[];
}

export function DataGrid(props: DataGridProps) {
  const {
    apiRef,
    detector,
    isDetectorLoading,
    imageQueryUrl,
    columnVisibilityModel,
    onChangeColumnVisibilityModel,
    filterState,
    onFilterStateChange,
    onChangeCursorPagination,
    filterableColumns,
  } = props;

  // State hooks
  const [selectedImageQuery, setSelectedImageQuery] = useState<ImageQuery | null>(null);
  const [selectedRowIndex, setSelectedRowIndex] = useState<number>(0);
  const [isQueryDetailsModalOpen, setIsQueryDetailsModalOpen] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [pinnedColumns, setPinnedColumns] = useState<GridPinnedColumns>({ left: ["image"] });
  const lastCount = useRef(0);
  const maxNewIQsTorefreshRef = useRef(DEFAULT_MAX_TO_REFRESH);

  // Hooks
  const theme = useTheme();
  const isSM = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useAppDispatch();

  // Redux state selectors
  const {
    data: { results: imageQueries, next, previous, count },
    loading: areLoadingImageQueries,
    page,
    pageSize,
  } = useAppSelector((state) => state.imageQueries);

  // Effect for updating pinned columns on responsive breakpoints
  useEffect(() => {
    setPinnedColumns(isSM ? {} : { left: ["image"] });
  }, [isSM]);

  const [allPosichecksCount, setAllPosichecksCount] = useState(0);
  const { get: getAllListPosichecks, loading: loadingAllListPosichecks } = useFetchCustom<ListResponse<ImageQuery>>(
    `${JANZU_URL}reef-api/image-queries`,
    {
      cachePolicy: CachePolicies.NO_CACHE,
    }
  );

  useEffect(() => {
    if (detector) {
      getAllListPosichecks(`?detector_id=${detector.id}`).then((allListPosicheck) => {
        setAllPosichecksCount(allListPosicheck.count);
      });
    } else {
      setAllPosichecksCount(0);
    }
  }, [getAllListPosichecks, detector]);

  const onOpenDeleteModal = useCallback((imageQuery: ImageQuery) => {
    setSelectedImageQuery(imageQuery);
    setIsDeleteModalOpen(true);
  }, []);

  const filteredColumns = useMemo(() => {
    const columnsOrderMap = new Map(filterableColumns.map((name, index) => [name, index]));

    return getColumns(detector)
      .filter((column) => filterableColumns.includes(column.field) || column.field === "image")
      .sort((itemA, itemB) => {
        if (itemA.field === "image") return 1;
        if (itemB.field === "image") return -1;

        const itemAOrder = columnsOrderMap.get(itemA.field) ?? 0;
        const itemBOrder = columnsOrderMap.get(itemB.field) ?? 0;

        return itemAOrder - itemBOrder;
      });
  }, [detector, filterableColumns]);

  const columns = useMemo(
    () => enhanceColumnWithDeleteModal(filteredColumns, onOpenDeleteModal),
    [filteredColumns, onOpenDeleteModal]
  );

  const handlePinnedColumnsChange = (updatedPinnedColumns: GridPinnedColumns) => {
    setPinnedColumns(updatedPinnedColumns);
  };

  const handleOpenQueryDetailsModal = () => {
    setIsQueryDetailsModalOpen(true);
  };

  const handleCloseQueryDetailsModal = () => {
    setIsQueryDetailsModalOpen(false);
  };

  const getIndex = useCallback(
    (selectedData: ImageQuery) => imageQueries.findIndex((imageQuery) => imageQuery?.iq_id === selectedData?.iq_id),
    [imageQueries]
  );

  const handleRowClick = (row: ImageQuery) => {
    const index = getIndex(row);
    handleOpenQueryDetailsModal();
    setSelectedRowIndex(index);
  };

  const handleChangePage = (newPage: number) => {
    // This validation is to prevent a weird behavior from DataGridPro: handleChangePage is
    // uncontrolledly executed when the page size changes and the value of page is
    // higher that the amount of maximun pages of the new page size
    if (Math.abs(page - newPage) > 1) {
      return;
    }
    lastCount.current = count;
    if (newPage > 0) {
      if (newPage > page) {
        onChangeCursorPagination({
          cursor: extractPaginateFrom(next || ""),
          direction: "next",
        });
      } else {
        onChangeCursorPagination({
          cursor: extractPaginateFrom(previous || ""),
          direction: "previous",
        });
      }
      dispatch(setPage(newPage));
    }
  };

  const handleChangePageSize = (newPageSize: number) => {
    onChangeCursorPagination({
      cursor: "",
      direction: "next",
    });
    dispatch(setPageSize(newPageSize));
  };

  const handleSortModelChange = (newSortModel: GridSortModel) => {
    const mappedField = columnUIToInternal(newSortModel[0].field);
    const currentOrdering = filterState.ordering || "";
    const isDescending = currentOrdering.startsWith("-");
    const currentField = isDescending ? currentOrdering.substring(1) : currentOrdering;
    let ordering = "";

    if (currentField === mappedField) {
      ordering = isDescending ? mappedField : `-${mappedField}`;
    } else {
      ordering = mappedField;
    }

    onFilterStateChange({ ordering });
  };

  const fetchImageQueries = async () => {
    lastCount.current = count;
    await dispatch(getImageQueries({ url: imageQueryUrl }));
  };

  const { del: deleteImageQuery, loading: isDeleteImageQueryLoading } = useFetchCustom(
    `${JANZU_URL}reef-api/image-queries/${selectedImageQuery?.iq_id}/delete`,
    {
      cachePolicy: CachePolicies.NO_CACHE,
    }
  );

  const handleDeleteRefetch = async () => {
    lastCount.current = count;
    if (imageQueries.length === 1) {
      if (previous === null && next !== null) {
        onChangeCursorPagination({
          cursor: extractPaginateFrom(next),
          direction: "next",
        });
        dispatch(setPage(1));
      } else if (previous !== null && next === null) {
        handleChangePage(page - 1);
      } else {
        await fetchImageQueries();
        handleCloseQueryDetailsModal();
      }
    } else {
      await fetchImageQueries();
      if (selectedRowIndex === imageQueries.length - 1) {
        setSelectedRowIndex((prev) => prev - 1);
      }
    }
  };

  const handleDeleteImageQuery = async () => {
    try {
      if (!selectedImageQuery) return;
      await deleteImageQuery();
      setIsDeleteModalOpen(false);
      await handleDeleteRefetch();
      enqueueSnackbar("Image Query successfully deleted!.", {
        variant: "success",
      });
    } catch (e) {
      console.error(e);
    }
  };

  // For refresh on new image queries
  const refreshImageQueries = useCallback(
    (key: SnackbarKey) => {
      lastCount.current = count;
      maxNewIQsTorefreshRef.current = DEFAULT_MAX_TO_REFRESH;
      onChangeCursorPagination({
        cursor: "",
        direction: "next",
      });
      dispatch(setPage(1));
      closeSnackbar(key);
      setSelectedRowIndex(0);
    },
    [onChangeCursorPagination, dispatch, count]
  );

  const action = useCallback(
    (key: SnackbarKey) => (
      <>
        <Button
          variant="outlined"
          className="text-base text-white font-semibold border-white py-2"
          onClick={() => refreshImageQueries(key)}
        >
          Refresh
        </Button>
        <IconButton
          className="text-xs text-white"
          onClick={() => {
            maxNewIQsTorefreshRef.current += DEFAULT_MAX_TO_REFRESH;
            closeSnackbar(key);
          }}
        >
          <CloseIcon fontSize="inherit" />
        </IconButton>
      </>
    ),
    [refreshImageQueries]
  );

  useEffect(() => {
    if (lastCount.current !== 0 && count >= lastCount.current + maxNewIQsTorefreshRef.current) {
      enqueueSnackbar("There are new images queries", {
        action,
        variant: "info",
        autoHideDuration: null,
      });
    }
  }, [action, count]);

  return (
    <Box sx={{ height: "100%" }}>
      <StyledDataGrid
        loading={areLoadingImageQueries || isDetectorLoading || loadingAllListPosichecks}
        paginationMode="server"
        filterMode="server"
        sortingMode="server"
        apiRef={apiRef}
        rows={imageQueries}
        getRowId={(row: GridRowModel) => row?.iq_id}
        columns={columns}
        pinnedColumns={pinnedColumns}
        onPinnedColumnsChange={handlePinnedColumnsChange}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={(newModel) => onChangeColumnVisibilityModel(newModel)}
        onRowClick={(params) => handleRowClick(params.row)}
        rowCount={count}
        localeText={{ noRowsLabel: !filterState.detector_id ? "Choose Detector" : "No Rows" }}
        pagination
        page={page - 1}
        pageSize={pageSize}
        onPageChange={(newPage) => handleChangePage(newPage + 1)}
        onPageSizeChange={(newPageSize: number) => handleChangePageSize(newPageSize)}
        onSortModelChange={handleSortModelChange}
        components={{
          NoRowsOverlay: allPosichecksCount === 0 ? NoRowsOverlay : undefined,
        }}
      />
      <ConfirmDeleteModal
        isDeleteModalOpen={isDeleteModalOpen}
        setIsDeleteModalOpen={setIsDeleteModalOpen}
        modalTitle="DELETE IMAGE QUERY"
        onRemoveHandler={handleDeleteImageQuery}
        isLoadingIQDelete={isDeleteImageQueryLoading}
        modalContent="<strong>Deleting the image will mean that it will not be used to train the detector</strong><br />Are you sure you want to continue?"
      />
      {isQueryDetailsModalOpen && detector && (
        <QueryDetailsModal
          loading={areLoadingImageQueries}
          getData={fetchImageQueries}
          rows={imageQueries}
          rowCount={count}
          selectedRowIndex={selectedRowIndex}
          setSelectedRowIndex={setSelectedRowIndex}
          showDialog={isQueryDetailsModalOpen}
          onClose={handleCloseQueryDetailsModal}
          page={page}
          pageSize={pageSize}
          setPage={handleChangePage}
          setPageSize={handleChangePageSize}
          detector={detector}
          onDeleteRefetch={handleDeleteRefetch}
        />
      )}
    </Box>
  );
}
